import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { apiUrl } from "features/configure";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useOutletContext } from "react-router";

// 語言選項配置
export const LANGUAGE_OPTIONS = [
  { label: "Auto detect", value: "" },
  { label: "English", value: "en" },
  { label: "Chinese (Traditional)", value: "zh-TW" },
  { label: "Chinese (Simplified)", value: "zh-CN" },
  { label: "Japanese", value: "ja" },
  { label: "Korean", value: "ko" },
  { label: "Vietnamese", value: "vi" },
  { label: "Thai", value: "th" },
  { label: "Indonesian", value: "id" },
  { label: "Malay", value: "ms" },
  { label: "Filipino", value: "tl" },
  { label: "Hindi", value: "hi" },
  { label: "Bengali", value: "bn" },
  { label: "Urdu", value: "ur" },
  { label: "Arabic", value: "ar" },
  { label: "Persian", value: "fa" },
  { label: "Hebrew", value: "he" },
  { label: "Turkish", value: "tr" },
  { label: "Russian", value: "ru" },
  { label: "Ukrainian", value: "uk" },
  { label: "French", value: "fr" },
  { label: "German", value: "de" },
  { label: "Spanish", value: "es" },
  { label: "Portuguese", value: "pt" },
  { label: "Italian", value: "it" },
  { label: "Dutch", value: "nl" },
  { label: "Polish", value: "pl" },
  { label: "Czech", value: "cs" },
  { label: "Romanian", value: "ro" },
  { label: "Hungarian", value: "hu" },
  { label: "Swedish", value: "sv" },
  { label: "Finnish", value: "fi" },
  { label: "Norwegian", value: "no" },
  { label: "Danish", value: "da" },
  { label: "Greek", value: "el" },
];

export const translationUtils = {
  // 獲取語言顯示名稱
  getLanguageLabel: (value) => {
    const option = LANGUAGE_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : "Select";
  },

  // 創建語言選項列表
  createLanguageMenuItems: (isFromMenu = false, onLanguageChange) => {
    const options = isFromMenu
      ? LANGUAGE_OPTIONS
      : LANGUAGE_OPTIONS.filter((opt) => opt.value !== "");

    return options.map((option) => ({
      children: option.label,
      func: () => onLanguageChange(option.value),
    }));
  },
};

export const useLanguageSelector = (
  initialFromLang = "",
  initialToLang = "en"
) => {
  const [fromLanguage, setFromLanguage] = useState(initialFromLang);
  const [toLanguage, setToLanguage] = useState(initialToLang);
  const [fromLangMenuOpen, setFromLangMenuOpen] = useState(false);
  const [toLangMenuOpen, setToLangMenuOpen] = useState(false);
  const fromLangRef = useRef(null);
  const toLangRef = useRef(null);

  const { getLanguageLabel, createLanguageMenuItems } = translationUtils;

  const handleFromLanguageChange = useCallback(
    (onLanguageChange) => {
      return (newFrom) => {
        setFromLanguage(newFrom);
        setFromLangMenuOpen(false);
        onLanguageChange?.(newFrom, toLanguage);
      };
    },
    [toLanguage]
  );

  const handleToLanguageChange = useCallback(
    (onLanguageChange) => {
      return (newTo) => {
        setToLanguage(newTo);
        setToLangMenuOpen(false);
        onLanguageChange?.(fromLanguage, newTo);
      };
    },
    [fromLanguage]
  );

  return {
    // 狀態
    fromLanguage,
    toLanguage,
    fromLangMenuOpen,
    toLangMenuOpen,
    fromLangRef,
    toLangRef,

    // 狀態更新函數
    setFromLanguage,
    setToLanguage,
    setFromLangMenuOpen,
    setToLangMenuOpen,

    // 工具函數
    getLanguageLabel,
    createLanguageMenuItems,
    handleFromLanguageChange,
    handleToLanguageChange,
  };
};

const useTranslation = () => {
  const [translationData, setTranslationData] = useState({});
  const [isTranslating, setIsTranslating] = useState({});
  const abortControllersRef = useRef({});
  const dispatchSnackbar = useDispatchSnackbar();
  const [entityName] = useOutletContext();

  // 原有的 translateMessage 保持不變，用於需要儲存狀態的場景
  const translateMessage = useCallback(
    async (messageId, message, from = "", to = "en") => {
      if (!message || !messageId || !entityName) return;

      // 針對同一個 messageId，取消之前的翻譯請求
      if (abortControllersRef.current[messageId]) {
        abortControllersRef.current[messageId].abort();
        delete abortControllersRef.current[messageId];
      }

      // 清除該 messageId 的舊翻譯狀態
      setIsTranslating((prev) => {
        const newState = { ...prev };
        Object.keys(newState).forEach((key) => {
          if (key.startsWith(`${messageId}_`)) {
            delete newState[key];
          }
        });
        return newState;
      });

      const translationKey = `${messageId}_${from}_${to}`;

      try {
        // 創建新的 AbortController
        const abortController = new AbortController();
        abortControllersRef.current[messageId] = abortController;

        setIsTranslating((prev) => ({ ...prev, [translationKey]: true }));

        const response = await axios.post(
          `${apiUrl}/api/translation/translate`,
          {
            text: message,
            sourceLanguage: from,
            targetLanguage: to,
            messageId: messageId,
            entityName: entityName,
          },
          {
            signal: abortController.signal,
          }
        );

        const translatedText = response.data.data.translatedText;

        // 針對同一個 messageId，覆蓋掉前一個翻譯的內容
        setTranslationData((prev) => {
          const newData = { ...prev };
          // 清除該 messageId 的所有舊翻譯
          Object.keys(newData).forEach((key) => {
            if (key.startsWith(`${messageId}_`)) {
              delete newData[key];
            }
          });
          // 設置新的翻譯
          newData[translationKey] = {
            originalText: message,
            translatedText,
            from,
            to,
            timestamp: new Date(),
          };
          return newData;
        });

        // 清理 AbortController
        delete abortControllersRef.current[messageId];

        return translatedText;
      } catch (error) {
        // 如果是被取消的請求，不顯示錯誤
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }

        console.error("Translation error:", error);
        dispatchSnackbar({
          err: error,
          message: "Translation failed. Please try again.",
        });
      } finally {
        setIsTranslating((prev) => ({ ...prev, [translationKey]: false }));
      }
    },
    [dispatchSnackbar, entityName]
  );

  // 新增：不儲存狀態的翻譯方法，專門用於輸入框翻譯
  const translateInputMessage = useCallback(
    async (message, from = "", to = "en", messageId = "") => {
      if (!message || !entityName) return;

      try {
        const response = await axios.post(
          `${apiUrl}/api/translation/translate`,
          {
            text: message,
            sourceLanguage: from,
            targetLanguage: to,
            messageId: messageId,
            entityName: entityName,
          }
        );

        return response.data.data.translatedText;
      } catch (error) {
        console.error("Translation error:", error);
        dispatchSnackbar({
          err: error,
          message: "Translation failed. Please try again.",
        });
        throw error;
      }
    },
    [dispatchSnackbar, entityName]
  );

  const getTranslation = useCallback(
    (messageId, from = "", to = "en") => {
      const translationKey = `${messageId}_${from}_${to}`;
      return translationData[translationKey];
    },
    [translationData]
  );

  const isTranslationLoading = useCallback(
    (messageId, from = "", to = "en") => {
      const translationKey = `${messageId}_${from}_${to}`;
      return isTranslating[translationKey] || false;
    },
    [isTranslating]
  );

  const clearTranslations = useCallback(() => {
    // 取消所有正在進行的翻譯請求
    Object.values(abortControllersRef.current).forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current = {};

    setTranslationData({});
    setIsTranslating({});
  }, []);

  return {
    translateMessage,
    translateInputMessage,
    getTranslation,
    isTranslationLoading,
    clearTranslations,
  };
};

export default useTranslation;
